import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { getCookie } from "../../../utility/getCookie";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

interface SellerInfo {
  name: string;
  address: string;
  country: string;
  pan: string;
  gst: string;
}

interface AddressInfo {
  name: string;
  address: string;
  country: string;
  stateCode: string;
  placeOfSupply?: string;
  placeOfDelivery?: string;
}

interface InvoiceItem {
  slNo: number;
  description: string;
  hsn: string;
  unitPrice: number;
  qty: number;
  netAmount: number;
  taxRate: number;
  taxType: string;
  taxAmount: number;
  totalAmount: number;
}

interface InvoiceTotals {
  subtotal: number;
  taxType: string;
  taxAmount: number;
  grandTotal: number;
  amountInWords: string;
}

interface InvoiceDetailsPayload {
  seller: SellerInfo;
  billingAddress: AddressInfo;
  shippingAddress: AddressInfo;
  orderNumber: string;
  orderDate: string;
  invoiceNumber: string;
  invoiceDetails: string;
  invoiceDate: string;
  items: InvoiceItem[];
  totals: InvoiceTotals;
}

const Invoice = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const [invoiceData, setInvoiceData] = useState<InvoiceDetailsPayload | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState<boolean>(false);

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPDF = async () => {
    const element = document.getElementById("invoice-capture-area");
    if (!element) return;

    setPdfLoading(true);
    try {
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff",
      });

      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");

      const imgWidth = 210;
      const pageHeight = 297;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      pdf.save(`invoice_${invoiceData?.invoiceNumber || orderId || "details"}.pdf`);
    } catch (err) {
      console.error("Error generating PDF:", err);
      alert("Failed to download PDF. Please try printing the page.");
    } finally {
      setPdfLoading(false);
    }
  };

  useEffect(() => {
    const fetchInvoiceDetails = async () => {
      if (!orderId) {
        setError("Order ID is missing.");
        setLoading(false);
        return;
      }

      try {
        const token = getCookie("access_token");
        const headers = { Authorization: `Bearer ${token}` };
        
        const BASE_URL = [
          process.env.REACT_APP_API_BASE_URL,
          process.env.REACT_APP_PAYMENT_APP_BACKEND_PREFIX || "/api/v1/payment"
        ].filter(Boolean).join("");

        const response = await axios.get(`${BASE_URL}/orders/${orderId}/invoice-details`, {
          headers,
          withCredentials: true,
        });

        if (response.data?.success && response.data?.data) {
          setInvoiceData(response.data.data);
        } else {
          setError("Failed to retrieve valid invoice details.");
        }
      } catch (err: any) {
        setError(err.response?.data?.message || err.message || "An error occurred while fetching invoice details.");
      } finally {
        setLoading(false);
      }
    };

    fetchInvoiceDetails();
  }, [orderId]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-50">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading Invoice...</span>
        </div>
      </div>
    );
  }

  if (error || !invoiceData) {
    return (
      <div className="alert alert-danger m-4 text-white" role="alert">
        {error || "Unable to display invoice details."}
      </div>
    );
  }

  const {
    seller,
    billingAddress,
    shippingAddress,
    orderNumber,
    orderDate,
    invoiceNumber,
    invoiceDetails,
    invoiceDate,
    items,
    totals,
  } = invoiceData;

  const qrContent = items
    .map((item) => `${item.slNo}. ${item.description} (Qty: ${item.qty})`)
    .join("\n");

  return (
    <>
      <style>{`
        @media print {
          .no-print {
            display: none !important;
          }
          body {
            background: white;
            color: black;
          }
        }
      `}</style>
      <div className="container mt-4 no-print">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <button onClick={() => window.history.back()} className="btn btn-outline-dark">
            <i className="bi bi-arrow-left me-2"></i> Back
          </button>
          <div>
            <button onClick={handlePrint} className="btn btn-outline-dark me-2">
              <i className="bi bi-printer me-2"></i> Print Invoice
            </button>
            <button onClick={handleDownloadPDF} className="btn btn-dark" disabled={pdfLoading}>
              {pdfLoading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Downloading...
                </>
              ) : (
                <>
                  <i className="bi bi-file-earmark-pdf me-2"></i> Download PDF
                </>
              )}
            </button>
          </div>
        </div>
      </div>
      <div id="invoice-capture-area">
        <div className="display-max">
          <section className="invoice-page">
          <header className="top">
            <div className="invoice-title">
              <div>
                <img src="../../assets/img/salarlogo.png" className="invoice-logo" alt="Salar Logo" />
              </div>
              <strong className="text-black">Invoice/Bill of Supply/</strong>
              <span className="text-sm text-black">(Original for Recipient)</span>
            </div>
          </header>
          <div className="address-grid">
            <div className="seller">
              <h3 className="text-black">Sold By :</h3>
              <div className="text-black">{seller.name}</div>
              <p className="text-black">{seller.address}</p>
              <div className="seller-meta">
                <div className="text-black">
                  <b>PAN No:</b> {seller.pan}
                </div>
                <div className="text-black">
                  <b>GST Registration No:</b> {seller.gst}
                </div>
                <div className="text-black">
                  <b>Dynamic QR Code:</b>
                </div>
                <img
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(qrContent)}`}
                  alt="Dynamic QR Code"
                  style={{ width: "20mm", height: "20mm", marginTop: "2mm", border: "1px solid #ddd" }}
                />
              </div>
            </div>
            <div className="addresses right">
              <div className="address-block">
                <h3 className="text-black">Billing Address :</h3>
                <div className="text-black">{billingAddress.name}</div>
                <div className="text-black" style={{ whiteSpace: "pre-wrap" }}>
                  {billingAddress.address}
                </div>
                <div className="text-black">{billingAddress.country}</div>
                <div className="text-black">
                  <b>State/UT Code:</b> {billingAddress.stateCode}
                </div>
              </div>
              <div className="address-block shipping">
                <h3 className="text-black">Shipping Address :</h3>
                <div className="text-black">{shippingAddress.name}</div>
                <div className="text-black" style={{ whiteSpace: "pre-wrap" }}>
                  {shippingAddress.address}
                </div>
                <div className="text-black">{shippingAddress.country}</div>
                <div className="text-black">
                  <b>State/UT Code:</b> {shippingAddress.stateCode}
                </div>
                {shippingAddress.placeOfSupply && (
                  <div className="text-black">
                    <b>Place of supply:</b> {shippingAddress.placeOfSupply}
                  </div>
                )}
                {shippingAddress.placeOfDelivery && (
                  <div className="text-black">
                    <b>Place of delivery:</b> {shippingAddress.placeOfDelivery}
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="order-grid">
            <div className="text-black">
              <div className="text-black">
                <b>Order Number:</b> {orderNumber}
              </div>
              <div className="text-black">
                <b>Order Date:</b> {orderDate}
              </div>
            </div>
            <div className="right">
              <div className="text-black">
                <b>Invoice Number :</b> {invoiceNumber}
              </div>
              <div className="text-black">
                <b>Invoice Details :</b> {invoiceDetails}
              </div>
              <div className="text-black">
                <b>Invoice Date :</b> {invoiceDate}
              </div>
            </div>
          </div>
          <table className="items product-table">
            <thead className="text-black">
              <tr>
                <th className="text-black">
                  Sl.
                  <br />
                  No
                </th>
                <th className="desc text-black">Description</th>
                <th className="text-black">
                  Unit
                  <br />
                  Price
                </th>
                <th className="text-black">Qty</th>
                <th className="text-black">
                  Net
                  <br />
                  Amount
                </th>
                <th className="text-black">
                  Tax
                  <br />
                  Rate
                </th>
                <th className="text-black">
                  Tax
                  <br />
                  Type
                </th>
                <th className="text-black">
                  Tax
                  <br />
                  Amount
                </th>
                <th className="text-black">
                  Total
                  <br />
                  Amount
                </th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr className="product-row" key={item.slNo}>
                  <td className="center text-black">{item.slNo}</td>
                  <td className="text-black">
                    {item.description}
                    <br />
                    HSN: {item.hsn}
                  </td>
                  <td className="text-black">₹{item.unitPrice.toFixed(2)}</td>
                  <td className="center text-black">{item.qty}</td>
                  <td className="text-black">₹{item.netAmount.toFixed(2)}</td>
                  <td className="text-black">{item.taxRate}%</td>
                  <td className="text-black">{item.taxType}</td>
                  <td className="text-black">₹{item.taxAmount.toFixed(2)}</td>
                  <td className="text-black">₹{item.totalAmount.toFixed(2)}</td>
                </tr>
              ))}
              <tr className="total-row">
                <td colSpan={7} className="text-black">
                  <b>TOTAL:</b>
                </td>
                <td className="text-black">
                  <b>₹{totals.taxAmount.toFixed(2)}</b>
                </td>
                <td className="text-black">
                  <b>₹{totals.grandTotal.toFixed(2)}</b>
                </td>
              </tr>
            </tbody>
          </table>
          <div className="words">
            <b className="text-black">Amount in Words:</b>
            <br />
            <strong className="text-black">{totals.amountInWords}</strong>
          </div>
          <div className="sign-box">
            <div className="sign-content text-black">
              <b>For {seller.name}:</b>
              <div className="signature scribble">D.Suraj Ahmed</div>
              <strong>Authorized Signatory</strong>
            </div>
          </div>
          <div className="reverse text-black">{seller.name}</div>
          <div className="reverse text-black">contact@salar.in</div>
          </section>
        </div>
      </div>
    </>
  );
};

export default Invoice;